import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { ISessionData, IUserProfile } from "@miplatform/types";
import { appStorage } from "@miplatform/utils";

type UserStoreState = {
  isAuthenticated: boolean;
  session: ISessionData;
  permissions: Set<string>;
  roleIds?: string[];
  profile: IUserProfile | null;
};

type UserStoreActions = {
  hasPermission: (permission: string) => boolean;
  hasSomePermissions: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  setIsAuthenticated: (isAuthenticated: boolean) => void;
  setSession: (session: ISessionData) => void;
  setPermissions: (permissions?: string[]) => void;
  setRoleIds: (roleIds: string[]) => void;
  getProfile: () => Promise<void>;
};

type UserStore = UserStoreState & UserStoreActions;

type PersistedUserStoreState = {
  session: ISessionData;
  permissions: string[];
  profile: IUserProfile | null;
  roleIds?: string[];
};

const initialState: UserStoreState = {
  isAuthenticated: false,
  session: { tenantId: null, userId: null },
  permissions: new Set(),
  roleIds: [],
  profile: null,
};

/** Coerce unknown persisted/API values into a safe string list. */
export const toPermissionsList = (permissions?: unknown): string[] =>
  Array.isArray(permissions)
    ? permissions.filter(
        (permission): permission is string => typeof permission === "string"
      )
    : [];

export const toPermissionsSet = (permissions?: unknown): Set<string> =>
  new Set(toPermissionsList(permissions));

export const useUserStore = create<UserStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      hasPermission: (permission: string): boolean => {
        return get().permissions.has(permission);
      },

      hasSomePermissions: (permissions: string[]): boolean => {
        const userPermissions = get().permissions;
        return permissions.some((permission) =>
          userPermissions.has(permission)
        );
      },

      hasAllPermissions: (permissions: string[]): boolean => {
        if (!Array.isArray(permissions) || permissions.length === 0) {
          return false;
        }
        const userPermissions = get().permissions;
        return permissions.every((permission) =>
          userPermissions.has(permission)
        );
      },

      setIsAuthenticated: (isAuthenticated: boolean) => {
        set({ isAuthenticated });
      },

      setSession: (session: ISessionData) => {
        set({ session });
      },

      setRoleIds: (roleIds: string[]) => {
        appStorage.setItem("roleIds", roleIds.join(","));
        set({ roleIds });
      },

      setPermissions: (permissions?: string[]) => {
        const permissionsList = toPermissionsList(permissions);
        appStorage.setItem("permissions", permissionsList.join(","));
        set({ permissions: toPermissionsSet(permissionsList) });
      },

      getProfile: async () => {
        const {
          session: { userId },
        } = get();

        if (userId) {
          const response = await subscriptionService.users.get(userId);
          if (response.status === HTTP_STATUSES.OK) {
            set({ profile: response.data });
          }
        }
      },
    }),
    {
      name: "userStore",
      storage: createJSONStorage(() => appStorage),
      partialize: (state): PersistedUserStoreState => ({
        session: state.session,
        permissions: Array.from(state.permissions),
        profile: state.profile,
        roleIds: state.roleIds,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as
          | Partial<PersistedUserStoreState>
          | undefined;

        return {
          ...currentState,
          ...persisted,
          permissions: toPermissionsSet(persisted?.permissions),
        };
      },
    }
  )
);

// Export initial state for testing
export const UserStoreInitialState = initialState;
